import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { agentsData } from "Stores/agentsData";
import { userDetail } from "Stores/userDetail";
import AgentSelect from "./AgentSelect";
import DayOfWeekField, { DEFAULT_DAYS, scheduleText } from "./DayOfWeekField";
import SequencePickerModal from "./SequencePickerModal";

const CreateAccountProspector = observer(() => {
  const history = useHistory();
  const [name, setName] = useState("");
  const [accountListId, setAccountListId] = useState("");
  const [accountListName, setAccountListName] = useState("");
  const [titles, setTitles] = useState("");
  const [perCompany, setPerCompany] = useState(5);
  const [sequenceId, setSequenceId] = useState("");
  const [sequenceName, setSequenceName] = useState("");
  const [seqModalOpen, setSeqModalOpen] = useState(false);
  const [runWhen, setRunWhen] = useState("DAILY"); // DAILY | WEEKLY | MANUAL
  const [time, setTime] = useState("08:00");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [dailyCap, setDailyCap] = useState(25);
  const [notifyMode, setNotifyMode] = useState("DAILY_DIGEST");
  const [saving, setSaving] = useState(false);
  const timezone = userDetail.timeZone || "UTC";

  useEffect(() => {
    agentsData.fetchAccountLists();
  }, []);

  const canSubmit = !!accountListId && !!sequenceId;
  const runText = runWhen === "MANUAL" ? "when you run it" : scheduleText(runWhen, time, days);

  const buildPayload = () => ({
    name: name.trim() || "Target accounts → people",
    accountListId,
    titles: titles.trim() || undefined,
    perCompany: Number(perCompany),
    sequenceId,
    dailyCap: Number(dailyCap),
    notifyMode,
    timezone,
    ...(runWhen === "MANUAL" ? {} : { frequency: runWhen, time, ...(runWhen === "WEEKLY" ? { days } : {}) })
  });

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    const agent = await agentsData.createAccountProspector(buildPayload());
    setSaving(false);
    if (agent && agent.id) history.push(`/agents/${agent.id}`);
  };

  const customize = async () => {
    setSaving(true);
    const draft = await agentsData.composeTemplate("account-prospector", buildPayload());
    setSaving(false);
    if (draft) { agentsData.stageDraft(draft); history.push("/agents/new/custom"); }
  };

  const step = node => (<li><span className="ck">✓</span><span>{node}</span></li>);

  return (
    <div className="ag-page">
      <div className="ag-page__head">
        <div>
          <button className="ag-link" onClick={() => history.push("/agents/new")}>← Back</button>
          <h1 className="ag-page__title">Hire · Target accounts → people</h1>
          <p className="ag-page__sub">Work an account list: find the buying committee at each target account, enrich them, and enroll them into a sequence.</p>
        </div>
      </div>

      <div className="ag-setup">
        <div className="ag-card-panel">
          <div className="ag-fsec">Target accounts</div>
          <label className="ag-field">
            <span className="ag-field__label">Account list<b className="ag-req">required</b></span>
            <AgentSelect
              searchable placeholder="Select an account list…"
              value={accountListId}
              onChange={v => { const o = (agentsData.accountLists || []).find(x => x.id === v); setAccountListId(v); setAccountListName(o ? o.name : v); }}
              options={(agentsData.accountLists || []).map(l => ({ value: l.id, label: l.count ? `${l.name} · ${l.count}` : l.name }))}
            />
            <span className="ag-hint">Accounts you've curated in Account Lists. None here? Build one there first.</span>
          </label>
          <label className="ag-field">
            <span className="ag-field__label">Job titles to target <span className="ag-hint" style={{ fontWeight: 400 }}>(optional)</span></span>
            <input className="ag-input" value={titles} placeholder="e.g. VP Sales, Head of Marketing, RevOps" onChange={e => setTitles(e.target.value)} />
            <span className="ag-hint">Comma-separated. Leave blank to pull all decision-makers we can find at each account.</span>
          </label>
          <label className="ag-field">
            <span className="ag-field__label">People per account</span>
            <input className="ag-input" type="number" min="1" value={perCompany} onChange={e => setPerCompany(e.target.value)} />
          </label>
          <label className="ag-field">
            <span className="ag-field__label">Agent name</span>
            <input className="ag-input" value={name} placeholder="Target accounts → people" onChange={e => setName(e.target.value)} />
          </label>

          <div className="ag-fsec">What happens next</div>
          <label className="ag-field">
            <span className="ag-field__label">Enroll into sequence<b className="ag-req">required</b></span>
            <button type="button" className="ag-select__btn" onClick={() => setSeqModalOpen(true)}>
              <span className={sequenceName ? "" : "ag-select__ph"}>{sequenceName || "Select a sequence…"}</span>
              <svg className="ag-select__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
            </button>
            <span className="ag-hint">People are deduped and undeliverables dropped first; email sequences enrich the email before enrolling.</span>
          </label>

          <div className="ag-fsec">Schedule</div>
          <div className="ag-field-row">
            <label className="ag-field"><span className="ag-field__label">Run</span>
              <AgentSelect value={runWhen} onChange={setRunWhen} options={[
                { value: "DAILY", label: "Every day" },
                { value: "WEEKDAYS", label: "Weekdays (Mon–Fri)" },
                { value: "WEEKLY", label: "Specific days" },
                { value: "MANUAL", label: "Only when I run it" }
              ]} />
            </label>
            {runWhen === "WEEKLY" && <DayOfWeekField value={days} onChange={setDays} />}
            {runWhen !== "MANUAL" && (
              <label className="ag-field"><span className="ag-field__label">At</span>
                <input className="ag-input" type="time" value={time} onChange={e => setTime(e.target.value)} />
              </label>
            )}
            <label className="ag-field"><span className="ag-field__label">People / run</span>
              <input className="ag-input" type="number" min="1" value={dailyCap} onChange={e => setDailyCap(e.target.value)} />
            </label>
          </div>

          <div className="ag-fsec">Notifications</div>
          <label className="ag-field">
            <span className="ag-field__label">Notify me</span>
            <AgentSelect value={notifyMode} onChange={setNotifyMode} options={[
              { value: "DAILY_DIGEST", label: "Daily digest — one summary a day" },
              { value: "ON_FAILURE", label: "Only when a run fails" },
              { value: "OFF", label: "Off — don't email me" }
            ]} />
          </label>
        </div>

        <div className="ag-rail">
          <div className="ag-summary">
            <h4>⚡ What this agent does</h4>
            <ul className="ag-slist">
              {step(<span>Works <b>{accountListName || "your target account list"}</b>, {runText}</span>)}
              {step(<span>Finds {titles.trim() ? <b>{titles.trim()}</b> : "the buying committee"} at each account</span>)}
              {step(<span>Skips anyone already processed or on DNC / undeliverable</span>)}
              {step(<span>Finds &amp; verifies emails (for email sequences)</span>)}
              {sequenceId && step(<span>Enrolls into <b>{sequenceName}</b></span>)}
            </ul>
          </div>
          <button className="ag-btn ag-btn--primary" style={{ justifyContent: "center" }} disabled={!canSubmit || saving} onClick={submit}>
            {saving ? "Creating…" : "Create agent →"}
          </button>
          <button className="ag-btn" style={{ justifyContent: "center" }} disabled={saving} onClick={customize}>Customize steps →</button>
          <button className="ag-btn" style={{ justifyContent: "center" }} onClick={() => history.push("/agents")}>Cancel</button>
          {!canSubmit && <p className="ag-hint" style={{ textAlign: "center" }}>Pick an account list and a sequence to create — or “Customize steps” to edit the flow first.</p>}
        </div>
      </div>

      <SequencePickerModal
        open={seqModalOpen}
        currentId={sequenceId}
        onSelect={s => { setSequenceId(s.id); setSequenceName(s.name); }}
        onClose={() => setSeqModalOpen(false)}
      />
    </div>
  );
});

export default CreateAccountProspector;
